import { QueryKeys } from "@Constants";
import { IActivityDetails } from "@Models/IActivityDetails";
import { IAssociatedStatusesKeys } from "@Models/IQuestionnaireDetails";
import QuestionnaireService from "@Services/QuestionnaireService";
import { useQuery } from "@tanstack/react-query";
import { getUserOption } from "@Utils";
import { useState } from "react";
import useQuestionnaireSelection from "./useQuestionnaireSelection";
import useQuestionnaireInstance from "@Components/questionnaires/hooks/useQuestionnaireInstance";
import { useActivityCategoriesTree } from "@Components/shared/hooks/useActivityCategoriesTree";

interface IUseAssociatedStatusesProps {
    activities: IActivityDetails[];
    loggedInUser: string;
}

export interface IProgressMetric {
    /**
     * The total activities available for points to be earned
     */
    total: {
        activities: number;
        points: number;
    };
    /**
     * The total activities available for points to be earned which are the logged in users activities
     */
    loggedInUserAvailable: {
        activities: number;
        points: number;
    };
    /**
     * The total number of activities with points earned
     */
    earned: {
        activities: number;
        points: number;
    };
}

export interface IAssociatedStatuses {
    percentage?: number;
    status: IAssociatedStatusesKeys;
    ownerId?: string;
    points?: number;
}

const useAssociatedStatuses = ({ activities, loggedInUser }: IUseAssociatedStatusesProps) => {
    const hasCMEOption = getUserOption("enable-cme-points-tracking") === "true";
    const [associatedStatusesMap, setAssociatedStatusesMap] = useState<Map<string, IAssociatedStatuses>>(new Map());
    const [progress, setProgress] = useState<IProgressMetric>({
        total: {
            activities: 0,
            points: 0,
        },
        loggedInUserAvailable: {
            activities: 0,
            points: 0,
        },
        earned: {
            activities: 0,
            points: 0,
        },
    });
    const questionnaireService = new QuestionnaireService();

    // Get unique activity ids, category ids and instance ids
    const activityIds = [...new Set(activities.map((a) => a.activityId))];
    const activityCategoryIds = [...new Set(activities.map((a) => a.activityCategoryId))];
    const activityInstanceIds = [...new Set(activities.map((a) => a.activityInstanceId))];

    //find all questionnaires of activity type custom
    const activityCategoriesTree = useActivityCategoriesTree();
    const customActivityCategory = activityCategoriesTree.find((category) => category.title === "Custom");

    const customActivityCategoryId =
        customActivityCategory && customActivityCategory.value.includes("cat-")
            ? customActivityCategory.value.replace("cat-", "")
            : null;

    const customActivityId = customActivityCategory?.children?.find((category) => category.title === "Custom")?.value;

    const { questionnaireAssociationsActive } = useQuestionnaireInstance({
        activityId: customActivityId,
        activityCategoryId: customActivityCategoryId,
    });

    const { questionnaireMinimal } = useQuestionnaireSelection();

    const isEnabled = hasCMEOption && (activityInstanceIds?.length > 0 ?? false);

    // ** Queries ** //

    const { isFetched } = useQuery(
        [QueryKeys.ActivityProgression, activities],
        async () => {
            const result = await questionnaireService.getAssociatedStatuses(
                activityIds,
                activityCategoryIds,
                activityInstanceIds
            );

            if (!result.hasErrors) {
                const newMap = new Map<string, IAssociatedStatuses>();

                activities.forEach((activity) => {
                    let questionnairePoints;
                    const matchedActivityCategory = result.value.notStarted.activityCategories.find(
                        (category) => category.activityCategoryId === activity.activityCategoryId
                    );

                    //for cme public activities, questionnaire can be assigned to not use the earliest created
                    if (activity.activityMeta?.questionnaireId) {
                        //check if assigned questionnaire hasn't had activity type reassigned (i.e is still of activity type custom)
                        const questionnaireAssociation = questionnaireAssociationsActive.find(
                            (questionnaire) => questionnaire.questionnaireId === activity.activityMeta?.questionnaireId
                        );
                        //get points of the questionnaire
                        const questionnaire = questionnaireMinimal.find(
                            (questionnaire) =>
                                questionnaire.questionnaireId === questionnaireAssociation?.questionnaireId
                        );
                        questionnairePoints = questionnaire?.points;
                    }

                    if (matchedActivityCategory) {
                        newMap.set(activity.activityInstanceId, {
                            status: "notStarted",
                            ownerId: activity.userId,
                            points: questionnairePoints ? questionnairePoints : matchedActivityCategory.points,
                        });
                        return;
                    }

                    const matchedActivity = result.value.notStarted.activities.find(
                        (a) => a.activityId === activity.activityId
                    );

                    if (matchedActivity) {
                        newMap.set(activity.activityInstanceId, {
                            status: "notStarted",
                            ownerId: activity.userId,
                            points: questionnairePoints ? questionnairePoints : matchedActivity.points,
                        });
                    }
                    return null;
                });

                result.value.inProgress.forEach(({ activityInstanceId, percentage, points }) => {
                    const activity = activities.find((activity) => activity.activityInstanceId === activityInstanceId);
                    newMap.set(activityInstanceId, {
                        percentage,
                        status: "inProgress",
                        ownerId: activity.userId,
                        points,
                    });
                });

                const uniqueCompleted = [
                    ...new Map(result.value.completed.map((item) => [item.activityInstanceId, item])).values(),
                ];

                uniqueCompleted.forEach((activity) => {
                    newMap.set(activity.activityInstanceId, { status: "completed", points: activity.points });
                });
                setAssociatedStatusesMap(newMap);

                // Helper function to calculate count and points
                const calculateMetrics = (activities, filterFn: (a) => boolean) => {
                    const filtered = activities.filter(filterFn);
                    return {
                        count: filtered.length,
                        points: filtered.reduce((sum, a) => sum + a.points, 0),
                    };
                };
                const newMapArray = Array.from(newMap.values());

                const pointMetrics = {
                    loggedInUser: {
                        notStarted: calculateMetrics(
                            newMapArray,
                            (a) => a.status === "notStarted" && a.ownerId === loggedInUser
                        ),
                        inProgress: calculateMetrics(
                            newMapArray,
                            (a) => a.status === "inProgress" && a.ownerId === loggedInUser
                        ),
                    },
                    total: {
                        notStarted: calculateMetrics(newMapArray, (a) => a.status === "notStarted"),
                        inProgress: calculateMetrics(newMapArray, (a) => a.status === "inProgress"),
                        completed: {
                            count: uniqueCompleted.length,
                            points: uniqueCompleted.reduce((sum, a) => sum + a.points, 0),
                        },
                    },
                };

                const progressMetrics = {
                    total: {
                        activities:
                            pointMetrics.total.notStarted.count +
                            pointMetrics.total.inProgress.count +
                            pointMetrics.total.completed.count,
                        points:
                            pointMetrics.total.notStarted.points +
                            pointMetrics.total.inProgress.points +
                            pointMetrics.total.completed.points,
                    },
                    loggedInUserAvailable: {
                        activities:
                            pointMetrics.loggedInUser.notStarted.count + pointMetrics.loggedInUser.inProgress.count,
                        points:
                            pointMetrics.loggedInUser.notStarted.points + pointMetrics.loggedInUser.inProgress.points,
                    },
                    earned: {
                        activities: pointMetrics.total.completed.count,
                        points: pointMetrics.total.completed.points,
                    },
                };

                if (progressMetrics.total.points > 0) {
                    setProgress(progressMetrics);
                }
            }
            return null;
        },
        {
            enabled: isEnabled,
            refetchOnWindowFocus: false,
        }
    );

    return {
        isHydrated: isFetched || !isEnabled,
        associatedStatusesMap,
        progress,
    };
};

export default useAssociatedStatuses;
